import { FC, useEffect, useMemo, useState } from 'react';
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, InfoCircle } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { DividendPayoutType } from '../../../hooks/dividend/dividend.types';
import { useShareClasses } from '../../../hooks/share-class/use-share-classes';
import { useInfiniteStakeholders } from '../../../hooks/stakeholder/use-infinite-stakeholders';
import { StakeholderOrder } from '../../../types/stakeholder.types';
import { getS3FileOriginalName } from '../../../utils/get-s3file-original-name.util';
import { getTranslation } from '../../../utils/get-translation.util';
import { AddDividendFormData, StakeholderDoc } from './add-dividend.validation';
import { DividendFileUpload, UploadedFile } from './dividend-file-upload';

type Props = {
  companyId: string;
  control: Control<AddDividendFormData>;
  getValues: UseFormGetValues<AddDividendFormData>;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
  setValue: UseFormSetValue<AddDividendFormData>;
};

export const AddDividendStepThree: FC<Props> = ({
  companyId,
  control,
  getValues,
  onBack,
  onCancel,
  onNext,
  setValue,
}) => {
  const type = useWatch({ control, name: 'type' });
  const rawItems = useWatch({ control, name: 'items' });
  const items = useMemo(() => rawItems ?? [], [rawItems]);

  const [expandedId, setExpandedId] = useState<null | string>(null);
  const [filesByStakeholder, setFilesByStakeholder] = useState<Record<string, UploadedFile[]>>(
    () => {
      const documents = getValues('stakeholderDocuments') ?? [];
      return documents.reduce<Record<string, UploadedFile[]>>((acc, document) => {
        acc[document.stakeholderId] = (document.filesLinks ?? []).map((link) => ({
          abort: () => {},
          docLink: link,
          id: link,
          loadProgress: 100,
          name: getS3FileOriginalName(link) || 'document',
        }));
        return acc;
      }, {});
    },
  );

  const { shareClasses } = useShareClasses({ companyId, filter: 'all', take: 200 });
  const { stakeholders: allStakeholders } = useInfiniteStakeholders({
    companyId,
    search: '',
    sortOrder: StakeholderOrder.ASC,
  });

  const impactedStakeholders: StakeholderDoc[] = useMemo(() => {
    if (type === DividendPayoutType.PER_STAKEHOLDER) {
      // Only the id is reliable: editing rebuilds the items from the saved payout, which carries no
      // stakeholder name, so the name is resolved here. One stakeholder can hold several share
      // classes in the same payout, hence the dedupe — otherwise they get an accordion each.
      const byStakeholderId = new Map<string, StakeholderDoc>();

      items.forEach((item) => {
        if (!item.stakeholderId || byStakeholderId.has(item.stakeholderId)) return;

        const fullName =
          item.stakeholderName ??
          allStakeholders.find(({ id }) => id === item.stakeholderId)?.fullName;

        if (!fullName) return;

        byStakeholderId.set(item.stakeholderId, {
          filesLinks: [],
          fullName,
          stakeholderId: item.stakeholderId,
        });
      });

      return [...byStakeholderId.values()];
    }

    const selectedClassIds = new Set(
      items.filter((item) => item.shareClassId).map((item) => item.shareClassId as string),
    );

    const stakeholderIdSet = new Set<string>();
    shareClasses
      .filter((sc) => selectedClassIds.has(sc.id))
      .forEach((sc) => {
        (sc.stakeholderIds ?? []).forEach((id) => stakeholderIdSet.add(id));
      });

    return allStakeholders
      .filter((sh) => stakeholderIdSet.has(sh.id))
      .map((sh) => ({
        filesLinks: [],
        fullName: sh.fullName,
        stakeholderId: sh.id,
      }));
  }, [type, items, shareClasses, allStakeholders]);

  // Changing a share class can drop stakeholders from the payout. Their uploads stay in form state
  // otherwise, so the next step still submits files for people this payout no longer pays.
  useEffect(() => {
    if (impactedStakeholders.length === 0) return;

    const impactedIds = new Set(impactedStakeholders.map(({ stakeholderId }) => stakeholderId));
    const documents = getValues('stakeholderDocuments') ?? [];
    const scoped = documents.filter(({ stakeholderId }) => impactedIds.has(stakeholderId));

    if (scoped.length !== documents.length) setValue('stakeholderDocuments', scoped);

    setFilesByStakeholder((prev) =>
      Object.keys(prev).every((stakeholderId) => impactedIds.has(stakeholderId))
        ? prev
        : Object.fromEntries(
            Object.entries(prev).filter(([stakeholderId]) => impactedIds.has(stakeholderId)),
          ),
    );
  }, [impactedStakeholders, getValues, setValue]);

  const getFilesForStakeholder = (stakeholderId: string): UploadedFile[] =>
    filesByStakeholder[stakeholderId] ?? [];

  const handleFilesChange = (stakeholderId: string, fullName: string, files: UploadedFile[]) => {
    setFilesByStakeholder((prev) => ({ ...prev, [stakeholderId]: files }));

    const links = files.map((f) => f.docLink).filter(Boolean);
    const current = getValues('stakeholderDocuments') ?? [];
    const existing = current.find((d) => d.stakeholderId === stakeholderId);
    if (existing) {
      setValue(
        'stakeholderDocuments',
        current.map((d) => (d.stakeholderId === stakeholderId ? { ...d, filesLinks: links } : d)),
      );
    } else {
      setValue('stakeholderDocuments', [
        ...current,
        { filesLinks: links, fullName, stakeholderId },
      ]);
    }
  };

  return (
    <>
      <div className="invisible-y-scroll flex min-h-0 flex-1 flex-col overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white">
        <div className="flex flex-col gap-4 px-6 pb-6 pt-4">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-[450] text-gray-500">
              {getTranslation('DOCUMENTS_FOR_IMPACTED_STAKEHOLDERS')}
            </span>
            <InfoCircle className="size-3.5" iconColor="#667085" />
          </div>
          {impactedStakeholders.length === 0 ? (
            <p className="text-sm text-gray-500">
              {getTranslation('NO_IMPACTED_STAKEHOLDERS_FOUND')}
            </p>
          ) : (
            impactedStakeholders.map((sh) => {
              const isExpanded = expandedId === sh.stakeholderId;
              const files = getFilesForStakeholder(sh.stakeholderId);

              return (
                <div className="overflow-hidden rounded-md" key={sh.stakeholderId}>
                  <div
                    className={twMerge(
                      'flex cursor-pointer items-center justify-between bg-gray-50 p-4',
                      isExpanded ? 'rounded-t-md' : 'rounded-md',
                    )}
                    onClick={() => setExpandedId(isExpanded ? null : sh.stakeholderId)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-[450] text-gray-700">{sh.fullName}</span>
                      {files.length > 0 && (
                        <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-[550] text-brand-700">
                          {files.length}
                        </span>
                      )}
                    </div>
                    <ChevronDownIcon
                      className={twMerge(
                        'h-4 w-4 shrink-0 transition-transform duration-200',
                        isExpanded && 'rotate-180',
                      )}
                    />
                  </div>

                  {isExpanded && (
                    <div className="rounded-b-lg border border-gray-100 px-4 pb-3 pt-4">
                      <DividendFileUpload
                        counter={
                          files.length > 0 ? (
                            <AppFormattedMessage
                              id="COUNT_FILES_UPLOADED"
                              values={{ count: files.filter(({ docLink }) => docLink).length }}
                            />
                          ) : undefined
                        }
                        files={files}
                        filesFirst
                        onChange={(updated) =>
                          handleFilesChange(sh.stakeholderId, sh.fullName, updated)
                        }
                      />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="flex w-full shrink-0 items-center justify-between px-6">
        <Button
          className="h-9 w-fit bg-transparent px-3 py-1.5 text-sm font-[450] text-gray-700"
          onClick={onCancel}
          styleType="NONE"
          type="button"
        >
          {getTranslation('CANCEL')}
        </Button>
        <div className="flex items-center gap-3">
          <Button
            className="h-9 w-fit rounded border border-gray-300 bg-white px-4 py-2.5 text-sm font-[450] text-gray-700"
            onClick={onBack}
            styleType="NONE"
            type="button"
          >
            {getTranslation('BACK')}
          </Button>
          <Button
            className="h-9 w-fit px-4 py-2.5 text-sm font-[550] text-white"
            onClick={onNext}
            type="button"
          >
            {getTranslation('NEXT')}
          </Button>
        </div>
      </div>
    </>
  );
};
